import { Outlet } from 'react-router-dom';
import { useState, useCallback, UIEvent } from 'react';
import { useBreakpoint } from '@/shared/hooks/use-breakpoint';
import { Sidebar } from '@/app/layout/Sidebar';
import { Topbar } from '@/app/layout/Topbar';
import { MobileNav } from '@/app/layout/MobileNav';
import { TickerBanner } from '@/app/layout/TickerBanner';
import { features } from '@/config/flavor';
import '@/app/layout/AppShell.css';

/**
 * Application shell providing persistent layout around route content.
 *
 * - Desktop (>=840px): Sidebar + Topbar + main content area
 * - Mobile  (<840px):  Topbar + main content + bottom tab bar
 *
 * Uses the `useBreakpoint` hook from the shared hooks to determine
 * which layout mode to render.
 */
function AppShell() {
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'expanded' || breakpoint === 'large' || breakpoint === 'xlarge';

  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback((e: UIEvent<HTMLElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 10);
  }, []);

  if (isDesktop) {
    return (
      <div className="app-shell">
        <div className="app-shell-sidebar">
          <Sidebar />
        </div>
        <div className="app-shell-topbar">
          <Topbar isScrolled={isScrolled} />
        </div>
        {features.isIdf && (
          <div className={`app-shell-ticker ${isScrolled ? 'app-shell-ticker-scrolled' : ''}`}>
            <TickerBanner />
          </div>
        )}
        <main className="app-shell-main" onScroll={handleScroll}>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell-mobile">
      <div className="app-shell-topbar">
        <Topbar isScrolled={isScrolled} />
      </div>
      {features.isIdf && (
        <div className={`app-shell-ticker ${isScrolled ? 'app-shell-ticker-scrolled' : ''}`}>
          <TickerBanner />
        </div>
      )}
      <main className="app-shell-main" onScroll={handleScroll}>
        <Outlet />
      </main>
      <MobileNav />
    </div>
  );
}

export { AppShell };
